import React from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

// 数学模板专业版 - 二次函数（横屏 1920×1080）
const THEME = {
  primary: '#2E5BFF',
  primaryLight: '#6B8CFF',
  accent: '#FF6B6B',
  highlight: '#FFB347',
  bg: 'linear-gradient(135deg, #F0F4FF 0%, #E8EEFF 50%, #FFFFFF 100%)',
  cardShadow: '0 16px 40px rgba(46, 91, 255, 0.15)',
};

export const MathDemoPro: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: THEME.bg }}>
      <DecorativeBackground />

      <Sequence from={0} durationInFrames={150}>
        <IntroScene />
      </Sequence>

      <Sequence from={150} durationInFrames={150}>
        <TitleScene />
      </Sequence>

      <Sequence from={300} durationInFrames={1050}>
        <ExplanationScene />
      </Sequence>

      <Sequence from={1350} durationInFrames={300}>
        <QuizScene />
      </Sequence>

      <Sequence from={1650} durationInFrames={150}>
        <OutroScene />
      </Sequence>
    </AbsoluteFill>
  );
};

const DecorativeBackground: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill>
      {[
        { x: 120, y: 180, r: 140, c: THEME.primary, o: 0.05 },
        { x: 1720, y: 260, r: 180, c: THEME.accent, o: 0.06 },
        { x: 260, y: 900, r: 220, c: THEME.primaryLight, o: 0.05 },
        { x: 1700, y: 880, r: 120, c: THEME.highlight, o: 0.06 },
      ].map((c, i) => {
        const float = Math.sin((frame + i * 30) * 0.03) * 10;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: c.x - c.r,
              top: c.y - c.r + float,
              width: c.r * 2,
              height: c.r * 2,
              borderRadius: '50%',
              background: c.c,
              opacity: c.o,
              filter: 'blur(20px)',
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const IntroScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 12, stiffness: 120 } });
  const fadeText = interpolate(frame, [20, 50], [0, 1], { extrapolateRight: 'clamp' });
  const slideY = interpolate(frame, [20, 50], [30, 0], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', gap: 80 }}>
      <div style={{ fontSize: 240, transform: `scale(${scale}) rotate(${(1 - scale) * -20}deg)` }}>📐</div>
      <div style={{ opacity: fadeText, transform: `translateY(${slideY}px)` }}>
        <div style={{ fontSize: 120, fontWeight: 900, color: THEME.primary, letterSpacing: 4 }}>数学</div>
        <div style={{ fontSize: 32, color: '#555', marginTop: 16, letterSpacing: 6 }}>
          MATHEMATICS · 函数世界
        </div>
      </div>
    </AbsoluteFill>
  );
};

const TitleScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 14, stiffness: 110 } });
  const lineWidth = interpolate(frame, [30, 80], [0, 420], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div
        style={{
          padding: '72px 120px',
          background: '#fff',
          borderRadius: 32,
          boxShadow: THEME.cardShadow,
          transform: `scale(${scale})`,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 26, color: THEME.primaryLight, letterSpacing: 8, marginBottom: 16 }}>
          CHAPTER · 01
        </div>
        <div style={{ fontSize: 72, fontWeight: 900, color: THEME.primary, marginBottom: 24 }}>
          二次函数
        </div>
        <div style={{ fontSize: 36, color: '#333', fontWeight: 500, fontFamily: 'Georgia, serif' }}>
          y = ax² + bx + c
        </div>
        <div
          style={{
            height: 4,
            width: lineWidth,
            background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.accent})`,
            margin: '24px auto 0',
            borderRadius: 2,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

const ExplanationScene: React.FC = () => {
  const frame = useCurrentFrame();

  return (
    <AbsoluteFill style={{ padding: 60, flexDirection: 'row', gap: 40, alignItems: 'center' }}>
      {/* 左侧：坐标系可视化 */}
      <div
        style={{
          width: 1080,
          height: 820,
          background: '#fff',
          borderRadius: 24,
          position: 'relative',
          boxShadow: THEME.cardShadow,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '24px 32px', borderBottom: '1px solid #EEF1FF' }}>
          <div style={{ fontSize: 30, fontWeight: 700, color: THEME.primary }}>
            图像与性质 · 可视化实验
          </div>
          <div style={{ fontSize: 26, color: '#888', marginTop: 4 }}>
            y = x² - 2x - 3
          </div>
        </div>
        <CoordinateVisualization frame={frame} />
      </div>

      {/* 右侧：知识要点 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ fontSize: 26, color: THEME.primaryLight, letterSpacing: 4, fontWeight: 600 }}>
          KNOWLEDGE POINTS
        </div>
        <div style={{ fontSize: 42, fontWeight: 900, color: '#1a1a1a', lineHeight: 1.2, marginBottom: 12 }}>
          抛物线的
          <br />
          三大要素
        </div>

        {[
          { title: '开口方向', content: 'a > 0 开口向上；a < 0 开口向下', icon: '↕' },
          { title: '顶点坐标', content: '(-b/2a, (4ac-b²)/4a) 决定最值', icon: '◆' },
          { title: '对称轴', content: 'x = -b/2a 左右镜像对称', icon: '⊥' },
        ].map((point, i) => {
          const start = i * 200 + 100;
          const opacity = interpolate(frame, [start, start + 30], [0, 1], { extrapolateRight: 'clamp' });
          const x = interpolate(frame, [start, start + 30], [40, 0], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                padding: '24px 28px',
                background: '#fff',
                borderRadius: 16,
                opacity,
                transform: `translateX(${x}px)`,
                boxShadow: '0 4px 16px rgba(46, 91, 255, 0.08)',
                borderLeft: `4px solid ${THEME.primary}`,
                display: 'flex',
                alignItems: 'center',
                gap: 20,
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.primaryLight})`,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {point.icon}
              </div>
              <div>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#222', marginBottom: 4 }}>
                  {point.title}
                </div>
                <div style={{ fontSize: 22, color: '#666' }}>{point.content}</div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

const CoordinateVisualization: React.FC<{ frame: number }> = ({ frame }) => {
  const width = 1080;
  const height = 720;
  const originX = 540;
  const originY = 440;
  const unit = 80;

  const xAxisProgress = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });
  const yAxisProgress = interpolate(frame, [25, 55], [0, 1], { extrapolateRight: 'clamp' });
  const curveProgress = interpolate(frame, [60, 160], [0, 1], { extrapolateRight: 'clamp' });
  const vertexProgress = interpolate(frame, [170, 200], [0, 1], { extrapolateRight: 'clamp' });
  const symmetryProgress = interpolate(frame, [210, 250], [0, 1], { extrapolateRight: 'clamp' });

  // y = x² - 2x - 3；顶点 (1, -4)
  const points: [number, number][] = [];
  for (let x = -3; x <= 5; x += 0.05) {
    const y = x * x - 2 * x - 3;
    points.push([originX + x * unit, originY - y * unit * 0.5]);
  }
  const currentLen = Math.max(2, Math.floor(points.length * curveProgress));
  const pathD = points
    .slice(0, currentLen)
    .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
    .join(' ');

  const vertexX = originX + 1 * unit;
  const vertexY = originY - -4 * unit * 0.5;

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <defs>
        <pattern id="math-grid" width={unit} height={unit} patternUnits="userSpaceOnUse">
          <path d={`M ${unit} 0 L 0 0 0 ${unit}`} fill="none" stroke="#F0F3FF" strokeWidth="1" />
        </pattern>
        <linearGradient id="curve-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={THEME.accent} />
          <stop offset="100%" stopColor={THEME.primary} />
        </linearGradient>
      </defs>

      <rect width={width} height={height} fill="url(#math-grid)" />

      {/* X 轴 */}
      <line
        x1={60}
        y1={originY}
        x2={60 + (width - 120) * xAxisProgress}
        y2={originY}
        stroke="#333"
        strokeWidth="2.5"
      />
      {xAxisProgress >= 1 && (
        <>
          <polygon points={`${width - 60},${originY} ${width - 72},${originY - 6} ${width - 72},${originY + 6}`} fill="#333" />
          <text x={width - 60} y={originY + 30} fontSize="16" fill="#333" fontWeight="700">x</text>
        </>
      )}

      {/* Y 轴 */}
      <line
        x1={originX}
        y1={height - 40}
        x2={originX}
        y2={(height - 40) - (height - 100) * yAxisProgress}
        stroke="#333"
        strokeWidth="2.5"
      />
      {yAxisProgress >= 1 && (
        <>
          <polygon points={`${originX},60 ${originX - 6},72 ${originX + 6},72`} fill="#333" />
          <text x={originX + 12} y={70} fontSize="16" fill="#333" fontWeight="700">y</text>
        </>
      )}

      {/* 刻度 */}
      {xAxisProgress >= 1 && [-3, -2, -1, 1, 2, 3, 4, 5].map((n) => (
        <g key={`x${n}`}>
          <line x1={originX + n * unit} y1={originY - 4} x2={originX + n * unit} y2={originY + 4} stroke="#333" strokeWidth="2" />
          <text x={originX + n * unit} y={originY + 22} fontSize="13" fill="#666" textAnchor="middle">{n}</text>
        </g>
      ))}
      {yAxisProgress >= 1 && [-4, -2, 2, 4, 6].map((n) => (
        <g key={`y${n}`}>
          <line x1={originX - 4} y1={originY - n * unit * 0.5} x2={originX + 4} y2={originY - n * unit * 0.5} stroke="#333" strokeWidth="2" />
          <text x={originX - 12} y={originY - n * unit * 0.5 + 5} fontSize="13" fill="#666" textAnchor="end">{n}</text>
        </g>
      ))}
      {yAxisProgress >= 1 && (
        <text x={originX - 12} y={originY + 22} fontSize="13" fill="#666" textAnchor="end">O</text>
      )}

      {/* 对称轴 */}
      {symmetryProgress > 0 && (
        <g opacity={symmetryProgress}>
          <line
            x1={vertexX}
            y1={60}
            x2={vertexX}
            y2={height - 40}
            stroke={THEME.highlight}
            strokeWidth="2"
            strokeDasharray="6,6"
          />
          <rect x={vertexX + 8} y={70} width={80} height={26} rx={13} fill={THEME.highlight} />
          <text x={vertexX + 48} y={88} fontSize="18" fill="#fff" fontWeight="700" textAnchor="middle">x = 1</text>
        </g>
      )}

      {/* 抛物线 */}
      {curveProgress > 0 && (
        <path d={pathD} fill="none" stroke="url(#curve-grad)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      )}

      {/* 顶点 */}
      {vertexProgress > 0 && (
        <g opacity={vertexProgress}>
          <circle cx={vertexX} cy={vertexY} r={8 + vertexProgress * 4} fill="#fff" stroke={THEME.accent} strokeWidth="3" />
          <circle cx={vertexX} cy={vertexY} r={24} fill={THEME.accent} opacity={0.15}>
            <animate attributeName="r" values="16;28;16" dur="2s" repeatCount="indefinite" />
          </circle>
          <g transform={`translate(${vertexX + 20}, ${vertexY + 10})`}>
            <rect x={0} y={0} width={130} height={36} rx={8} fill={THEME.accent} />
            <text x={65} y={23} fontSize="16" fill="#fff" fontWeight="700" textAnchor="middle">顶点 (1, -4)</text>
          </g>
        </g>
      )}

      {/* 公式浮窗 */}
      {frame > 300 && (
        <g transform={`translate(${width - 280}, 90)`} opacity={interpolate(frame, [300, 340], [0, 1], { extrapolateRight: 'clamp' })}>
          <rect width={240} height={96} rx={14} fill="#fff" stroke={THEME.primary} strokeWidth="2" />
          <text x={120} y={36} fontSize="22" fill={THEME.primary} fontWeight="900" textAnchor="middle">
            y = x² − 2x − 3
          </text>
          <line x1={20} y1={50} x2={220} y2={50} stroke="#EEF1FF" strokeWidth="1" />
          <text x={120} y={75} fontSize="16" fill="#666" textAnchor="middle">
            顶点(1, -4) · 开口向上
          </text>
        </g>
      )}
    </svg>
  );
};

const QuizScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 14, stiffness: 110 } });
  const answerOpacity = interpolate(frame, [120, 160], [0, 1], { extrapolateRight: 'clamp' });
  const answerY = interpolate(frame, [120, 160], [20, 0], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div
        style={{
          padding: '64px 96px',
          background: '#fff',
          borderRadius: 28,
          boxShadow: THEME.cardShadow,
          transform: `scale(${scale})`,
          minWidth: 960,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
          <div
            style={{
              padding: '8px 20px',
              background: THEME.primary,
              color: '#fff',
              borderRadius: 20,
              fontSize: 26,
              fontWeight: 700,
            }}
          >
            Q · 互动测验
          </div>
          <div style={{ fontSize: 26, color: '#888' }}>检测你的掌握情况</div>
        </div>

        <div style={{ fontSize: 36, fontWeight: 700, color: '#1a1a1a', marginBottom: 40, lineHeight: 1.4 }}>
          二次函数 y = 2x² − 4x + 1 的<span style={{ color: THEME.primary }}>开口方向</span>是？
        </div>

        <div
          style={{
            opacity: answerOpacity,
            transform: `translateY(${answerY}px)`,
            padding: '28px 32px',
            background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
            borderRadius: 16,
            borderLeft: '6px solid #4CAF50',
          }}
        >
          <div style={{ fontSize: 28, color: '#4CAF50', fontWeight: 700, marginBottom: 8 }}>正确答案</div>
          <div style={{ fontSize: 28, color: '#2E7D32', fontWeight: 700 }}>
            向上（∵ a = 2 &gt; 0）
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

const OutroScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 12, stiffness: 110 } });
  const textOpacity = interpolate(frame, [30, 70], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', gap: 80 }}>
      <div style={{ fontSize: 220, transform: `scale(${scale})` }}>🎯</div>
      <div style={{ opacity: textOpacity }}>
        <div style={{ fontSize: 100, fontWeight: 900, color: THEME.primary }}>掌握了！</div>
        <div style={{ fontSize: 30, color: '#555', marginTop: 16, letterSpacing: 4 }}>
          继续探索数学的奇妙世界
        </div>
      </div>
    </AbsoluteFill>
  );
};
